import type { WorkflowDefinitionVersionDetail } from './platform-workflow-registry-diagnostics.models';
import type { WorkflowDefinitionVersionChangePreview } from './platform-workflow-definition-lifecycle.models';

export interface WorkflowVersionReviewAnswer {
  readonly answer: string;
  readonly detail: string;
  readonly tone: 'ready' | 'attention' | 'neutral';
}

export interface WorkflowVersionReviewPresentation {
  readonly publication: WorkflowVersionReviewAnswer;
  readonly runtime: WorkflowVersionReviewAnswer;
  readonly availability: WorkflowVersionReviewAnswer;
  readonly reasons: readonly string[];
  readonly nextAction: {
    readonly title: string;
    readonly detail: string;
    readonly target: 'lifecycle' | 'evidence';
  };
}

const reasonLabels: Readonly<Record<string, string>> = {
  lifecycle_not_active: 'The version is not active for new work.',
  runtime_unbound:
    'Process execution support is unavailable in this installation.',
  runtime_invalid: 'The workflow has validation errors.',
  effective_window_future: 'The saved registry window has not started.',
  effective_window_expired: 'The saved registry window has ended.',
  activation_missing: 'No activation was reported for this version.',
  'workflow.graph.activation_not_unique':
    'Activation does not uniquely select this version.',
  'workflow.graph.definition_invalid':
    'The saved workflow cannot be used for process runs.',
  'workflow.graph.execution_disabled': 'Saved workflow execution is disabled.',
  'workflow.timer.optional_stages_disabled':
    'Timers with optional stages are not enabled on this server.',
  'workflow.graph.action_dispatch_unavailable':
    'A service needed to run action triggers is unavailable.',
  'capability.unavailable': 'A required workflow module is unavailable.',
  'workflow.graph.cutover.legacy_parity_required':
    'Compatibility with the retained legacy workflow needs review.',
  'workflow.graph.cutover.subject_migration_required':
    'Legacy workflow subjects require migration before new work can use this version.',
  'workflow.graph.cutover.retained_binding_invalid':
    'Retained legacy work has invalid workflow or configuration bindings.',
};

/** Presents returned server axes; it does not evaluate dates or runtime selection. */
export function workflowVersionReviewPresentation(
  definition: Pick<
    WorkflowDefinitionVersionDetail,
    | 'statusCode'
    | 'runtimeBindingCode'
    | 'validationStatusCode'
    | 'isExecutable'
    | 'executabilityReasonCodes'
  >,
  versionChange?: Pick<
    WorkflowDefinitionVersionChangePreview,
    'actionCode'
  > | null,
): WorkflowVersionReviewPresentation {
  const status = normalize(definition.statusCode);
  const codes = [
    ...new Set(
      (definition.executabilityReasonCodes ?? []).map(normalizeReason),
    ),
  ];
  const unknownReasons = codes.some((code) => !reasonLabel(code));
  const hasSupportIssue = codes.some((code) =>
    [
      'runtime_unbound',
      'runtime_invalid',
      'workflow.graph.definition_invalid',
      'workflow.graph.execution_disabled',
      'workflow.timer.optional_stages_disabled',
      'workflow.graph.action_dispatch_unavailable',
      'capability.unavailable',
    ].includes(code),
  );
  const runtimeBound =
    normalize(definition.runtimeBindingCode) === 'runtime_bound';
  const runtimeValid = normalize(definition.validationStatusCode) === 'valid';
  const runtimeKnown =
    ['runtime_bound', 'metadata_only'].includes(
      normalize(definition.runtimeBindingCode),
    ) &&
    ['valid', 'invalid'].includes(normalize(definition.validationStatusCode));
  const hasWindowReason = codes.some((code) =>
    [
      'effective_window_future',
      'effective_window_expired',
      'activation_missing',
    ].includes(code),
  );
  const activationReasons = new Set([
    'lifecycle_not_active',
    'effective_window_future',
    'effective_window_expired',
    'activation_missing',
    'workflow.graph.activation_not_unique',
  ]);
  const reasons = codes
    .filter((code) => code !== 'lifecycle_not_active' || !hasWindowReason)
    .map(reasonLabel)
    .filter((label): label is string => label !== undefined);
  const pendingUpdate =
    status === 'published' &&
    !definition.isExecutable &&
    runtimeBound &&
    runtimeValid &&
    !hasSupportIssue &&
    !unknownReasons &&
    versionChange?.actionCode === 'make_current' &&
    codes.length > 0 &&
    codes.every((code) =>
      [
        'lifecycle_not_active',
        'activation_missing',
        'workflow.graph.activation_not_unique',
      ].includes(code),
    );
  if (pendingUpdate)
    reasons.splice(
      0,
      reasons.length,
      'This published version is ready to replace the current version for new runs.',
    );
  if (unknownReasons) reasons.push('Additional restrictions need review.');
  if (!definition.isExecutable && reasons.length === 0) {
    reasons.push(
      'The server did not return a reason. Inspect the reported status before changing availability.',
    );
  }

  const publication = publicationAnswer(status);
  const runtime: WorkflowVersionReviewAnswer = !runtimeKnown
    ? {
        answer: 'Process support needs review',
        detail:
          'The reported process support status was not recognized. Inspect the exact reported values before changing availability.',
        tone: 'attention',
      }
    : !runtimeBound
      ? {
          answer: 'Process support unavailable',
          detail:
            'This installation has not reported execution support for this version. Your platform administrator can review the reported restriction. Activation alone does not supply missing process support.',
          tone: 'attention',
        }
      : !runtimeValid || hasSupportIssue
        ? {
            answer: 'Process support needs attention',
            detail:
              'The server reports a validation, installation or required-module restriction. Review the reported reason before activating this version.',
            tone: 'attention',
          }
        : {
            answer: 'Process support ready',
            detail:
              'This version has validated process execution support. No technical workflow setup is required. Availability for new process runs is checked separately.',
            tone: 'ready',
          };
  const availability: WorkflowVersionReviewAnswer = definition.isExecutable
    ? {
        answer: 'Available for new work',
        detail:
          'The server reports this version eligible for runtime selection. Selection still depends on matching workflow, subject, scope, and configuration.',
        tone: 'ready',
      }
    : pendingUpdate
      ? {
          answer: 'Not current for new work',
          detail:
            'New runs use the current version until you make this version current. Existing runs keep their version.',
          tone: 'neutral',
        }
      : {
          answer: 'Cannot start new work',
          detail:
            'The server reports this version unavailable for new workflow instances.',
          tone: 'attention',
        };

  let nextAction: WorkflowVersionReviewPresentation['nextAction'];
  if (definition.isExecutable) {
    nextAction = {
      title: 'Before changing availability',
      detail:
        'Review the activation history and intended schedule below. Existing workflow instances remain attached to their recorded version.',
      target: 'lifecycle',
    };
  } else if (pendingUpdate) {
    nextAction = {
      title: 'Make this version current',
      detail:
        'Review and confirm Make current version below. New runs will use this version; existing runs keep their current version.',
      target: 'lifecycle',
    };
  } else if (unknownReasons || !runtimeKnown || codes.length === 0) {
    nextAction = {
      title: 'Inspect the reported restrictions',
      detail:
        'Review the exact status and restriction codes with an administrator before changing availability.',
      target: 'evidence',
    };
  } else if (hasSupportIssue || !runtimeBound || !runtimeValid) {
    nextAction = {
      title: 'Resolve process support checks',
      detail:
        'Review the reported validation or installation restriction with your platform administrator. Activation alone does not resolve missing process support.',
      target: 'evidence',
    };
  } else if (codes.some((code) => !activationReasons.has(code))) {
    nextAction = {
      title: 'Review the reported restrictions',
      detail:
        'The reported restrictions need review before changing activation. Inspect the exact codes with an administrator.',
      target: 'evidence',
    };
  } else if (!['published', 'active'].includes(status)) {
    nextAction = {
      title: 'Review the version status',
      detail:
        'Inspect the reported registry status before choosing a lifecycle action. Publication and activation are separate changes.',
      target: 'evidence',
    };
  } else if (
    codes.includes('effective_window_future') ||
    codes.includes('effective_window_expired')
  ) {
    nextAction = {
      title: 'Review the saved registry window',
      detail:
        'The reported restriction concerns the saved registry window. Changing activation records does not rewrite that window; inspect the saved values before choosing a lifecycle action.',
      target: 'evidence',
    };
  } else if (codes.includes('workflow.graph.activation_not_unique')) {
    nextAction = {
      title: 'Review activation selection',
      detail:
        'The server could not identify a unique matching activation for this version. Review activation history, scope, and configuration before changing the schedule.',
      target: 'lifecycle',
    };
  } else if (
    status === 'published' &&
    codes.includes('activation_missing') &&
    codes.every((code) =>
      ['activation_missing', 'lifecycle_not_active'].includes(code),
    )
  ) {
    nextAction = {
      title: 'Activate this version',
      detail:
        'Process support is ready. Choose when this published version becomes available for new process runs. An authorized user can review and confirm activation below; the server checks scope and conflicting windows.',
      target: 'lifecycle',
    };
  } else if (hasWindowReason && ['published', 'active'].includes(status)) {
    nextAction = {
      title: 'Review activation options',
      detail:
        'Review the reported window and activation history below. Available changes depend on your lifecycle permissions.',
      target: 'lifecycle',
    };
  } else {
    nextAction = {
      title: 'Review the version status',
      detail:
        'Inspect the reported registry status before choosing a lifecycle action. Publication and activation are separate changes.',
      target: 'evidence',
    };
  }

  return { publication, runtime, availability, reasons, nextAction };
}

function publicationAnswer(status: string): WorkflowVersionReviewAnswer {
  switch (status) {
    case 'published':
      return {
        answer: 'Published',
        detail:
          'This version is published. Activation is a separate change that controls eligibility for new work.',
        tone: 'ready',
      };
    case 'active':
      return {
        answer: 'Registered as active',
        detail:
          'The registry marks this version active. Its current availability for new work is reported separately.',
        tone: 'neutral',
      };
    case 'retired':
      return {
        answer: 'Registered as retired',
        detail:
          'The registry marks this version retired. Existing workflow instances keep their recorded version.',
        tone: 'neutral',
      };
    case 'draft':
      return {
        answer: 'Publication not confirmed',
        detail:
          'This registry record is marked draft. Publish through the draft review before treating it as a published version.',
        tone: 'attention',
      };
    default:
      return {
        answer: 'Publication status needs review',
        detail:
          'The returned registry status does not confirm publication. Inspect its exact value in the reported status.',
        tone: 'attention',
      };
  }
}

function normalizeReason(value: string): string {
  const normalized = normalize(value);
  const prefix = 'workflow.definition.';
  return normalized.startsWith(prefix)
    ? normalized.slice(prefix.length)
    : normalized;
}

function reasonLabel(code: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(reasonLabels, code)
    ? reasonLabels[code]
    : undefined;
}

function normalize(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}
